import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import '../index.css';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useContext(AuthContext);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    fetchArticle();
    fetchComments();
  }, [id]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/blogs/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setLoadingComments(true);
      const response = await axios.get(`/api/blogs/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      alert('Please log in to like this post');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/blogs/${id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setArticle({
        ...article,
        likesCount: response.data.likesCount,
        likes: response.data.liked 
          ? [...(article.likes || []), user._id]
          : (article.likes || []).filter(userId => userId !== user._id)
      });
    } catch (error) {
      console.error('Error liking blog:', error);
      alert('Failed to like post');
    }
  };

  const handleAddComment = async () => {
    if (!user) {
      alert('Please log in to comment');
      return;
    }

    if (!commentText.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/blogs/${id}/comments`, 
        { content: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setComments([response.data, ...comments]);
      setCommentText('');
      setArticle({ ...article, commentsCount: (article.commentsCount || 0) + 1 });
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/blogs/${id}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setComments(comments.filter(c => c._id !== commentId));
      setArticle({ ...article, commentsCount: Math.max(0, (article.commentsCount || 0) - 1) });
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
    }
  };

  const handleEdit = () => {
    navigate('/blog', { state: { editArticle: article } });
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/blog');
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Failed to delete blog post');
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>Loading blog post...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
        <h2 style={{ color: 'var(--text-accent)', marginBottom: '12px' }}>Blog post not found</h2>
        <button onClick={() => navigate('/blog')} style={{
          marginTop: '20px', padding: '10px 20px', backgroundColor: 'var(--primary)', color: 'white',
          border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px'
        }}>← Back to Blog</button>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
      <button onClick={() => navigate('/blog')} style={{
        padding: '10px 20px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
        borderRadius: '8px', color: 'var(--text)', cursor: 'pointer', fontSize: '14px', marginBottom: '30px',
        display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s ease'
      }} onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--primary)'; e.target.style.color = 'white'; }}
        onMouseLeave={(e) => { e.target.style.backgroundColor = 'var(--card-bg)'; e.target.style.color = 'var(--text)'; }}>
        ← Back to Blog
      </button>

      {isAdmin && (
        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px' }}>
          <button onClick={handleEdit} style={{
            padding: '8px 20px', backgroundColor: 'var(--primary)', color: 'white', border: 'none',
            borderRadius: '8px', cursor: 'pointer', fontSize: '14px'
          }}>Edit</button>
          <button onClick={handleDelete} style={{
            padding: '8px 20px', backgroundColor: '#ff4757', color: 'white', border: 'none',
            borderRadius: '8px', cursor: 'pointer', fontSize: '14px'
          }}>Delete</button>
        </div>
      )}

      <article style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '16px', padding: '50px 40px',
        border: '1px solid var(--border)', animation: 'fadeInUp 0.6s ease-out'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>{article.image}</div>
          <span style={{
            display: 'inline-block', padding: '6px 16px', backgroundColor: 'var(--primary)', color: 'white',
            borderRadius: '20px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase',
            letterSpacing: '1px', marginBottom: '16px'
          }}>{article.category}</span>
          <h1 style={{
            color: 'var(--text-accent)', fontSize: '36px', fontWeight: '800',
            marginBottom: '16px', lineHeight: '1.3'
          }}>{article.title}</h1>
          <div style={{
            color: 'var(--text-secondary)', fontSize: '14px', display: 'flex',
            gap: '16px', justifyContent: 'center', flexWrap: 'wrap'
          }}>
            <span>By {article.authorName}</span>
            <span>•</span>
            <span>{new Date(article.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>

        <div 
          className="blog-content"
          style={{ color: 'var(--text)', fontSize: '16px', lineHeight: '1.8' }}
          dangerouslySetInnerHTML={{ __html: article.content }} 
        />

        {/* Like Section */}
        <div style={{
          marginTop: '40px', paddingTop: '30px', borderTop: '2px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: '20px'
        }}>
          <button 
            onClick={handleLike}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px',
              backgroundColor: user && article.likes?.includes(user._id) ? 'var(--primary)' : 'var(--card-bg)',
              color: user && article.likes?.includes(user._id) ? 'white' : 'var(--text)',
              border: '2px solid var(--primary)', borderRadius: '8px', cursor: 'pointer',
              fontSize: '16px', fontWeight: '600', transition: 'all 0.3s ease'
            }}
          >
            <span style={{ fontSize: '20px' }}>
              {user && article.likes?.includes(user._id) ? '❤️' : '🤍'}
            </span>
            <span>{article.likesCount || 0} {(article.likesCount || 0) === 1 ? 'Like' : 'Likes'}</span>
          </button>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            💬 {article.commentsCount || 0} {(article.commentsCount || 0) === 1 ? 'Comment' : 'Comments'}
          </div>
        </div>
      </article>

      {/* Comments Section */}
      <div style={{
        marginTop: '40px', backgroundColor: 'var(--card-bg)', borderRadius: '16px',
        padding: '40px', border: '1px solid var(--border)'
      }}>
        <h3 style={{ color: 'var(--text-accent)', fontSize: '24px', marginBottom: '24px', fontWeight: '700' }}>
          Comments ({comments.length})
        </h3>

        {/* Add Comment */}
        {user ? (
          <div style={{ marginBottom: '30px' }}>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Share your thoughts..."
              style={{
                width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)',
                backgroundColor: 'var(--card-bg-secondary)', color: 'var(--text)', fontSize: '15px',
                minHeight: '100px', resize: 'vertical', fontFamily: 'inherit'
              }}
            />
            <button
              onClick={handleAddComment}
              disabled={!commentText.trim()}
              style={{
                marginTop: '12px', padding: '10px 24px', backgroundColor: 'var(--primary)',
                color: 'white', border: 'none', borderRadius: '8px', cursor: commentText.trim() ? 'pointer' : 'not-allowed',
                fontSize: '15px', fontWeight: '600', opacity: commentText.trim() ? 1 : 0.5
              }}
            >
              Post Comment
            </button>
          </div>
        ) : (
          <div style={{
            padding: '20px', backgroundColor: 'var(--card-bg-secondary)', borderRadius: '8px',
            marginBottom: '30px', textAlign: 'center', color: 'var(--text-secondary)'
          }}>
            Please <a href="/login" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>log in</a> to comment
          </div>
        )}

        {/* Comments List */}
        {loadingComments ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '40px', color: 'var(--text-secondary)',
            backgroundColor: 'var(--card-bg-secondary)', borderRadius: '8px'
          }}>
            No comments yet. Be the first to share your thoughts!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {comments.map(comment => (
              <div key={comment._id} style={{
                padding: '20px', backgroundColor: 'var(--card-bg-secondary)', borderRadius: '12px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text-accent)', marginBottom: '4px' }}>
                      {comment.username}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {new Date(comment.createdAt).toLocaleDateString('en-US', { 
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                      })}
                      {comment.isEdited && <span style={{ marginLeft: '8px' }}>(edited)</span>}
                    </div>
                  </div>
                  {user && (user._id === comment.userId._id || isAdmin) && (
                    <button
                      onClick={() => handleDeleteComment(comment._id)}
                      style={{
                        padding: '6px 12px', backgroundColor: '#ff4757', color: 'white',
                        border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div style={{ color: 'var(--text)', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {comment.content}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .blog-content h1 { color: var(--text-accent); font-size: 32px; font-weight: 800; margin-top: 40px; margin-bottom: 20px; }
        .blog-content h2 { color: var(--text-accent); font-size: 28px; font-weight: 700; margin-top: 36px; margin-bottom: 18px; }
        .blog-content h3 { color: var(--text-accent); font-size: 24px; font-weight: 700; margin-top: 32px; margin-bottom: 16px; }
        .blog-content h4 { color: var(--primary); font-size: 20px; font-weight: 600; margin-top: 24px; margin-bottom: 12px; }
        .blog-content h5 { color: var(--primary); font-size: 18px; font-weight: 600; margin-top: 20px; margin-bottom: 10px; }
        .blog-content p { margin-bottom: 16px; color: var(--text); line-height: 1.8; }
        .blog-content a { color: var(--primary); text-decoration: underline; }
        .blog-content a:hover { color: var(--text-accent); }
        .blog-content ul, .blog-content ol { margin-bottom: 16px; padding-left: 30px; color: var(--text); }
        .blog-content li { margin-bottom: 8px; line-height: 1.6; }
        .blog-content strong { font-weight: 700; color: var(--text-accent); }
        .blog-content em { font-style: italic; }
        .blog-content hr { border: none; border-top: 2px solid var(--border); margin: 32px 0; }
        .blog-content blockquote { border-left: 4px solid var(--primary); padding-left: 20px; margin: 20px 0; font-style: italic; color: var(--text-secondary); }
        .blog-content code { background-color: var(--card-bg-secondary); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 14px; }
        .blog-content pre { background-color: var(--card-bg-secondary); padding: 16px; border-radius: 8px; overflow-x: auto; margin-bottom: 16px; }
        .blog-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 20px 0; }
      `}</style>
    </div>
  );
};

export default BlogPost;
